import type { DateDifferencePart, Port } from "./types.js";

const DAY_MS = 86_400_000;
const OUTPUT_PARTS: Record<string, DateDifferencePart> = {
  output: "years",
  outputYears: "years",
  outputMonths: "months",
  outputDays: "days",
};

export function dateDifferencePart(
  portId: string,
): DateDifferencePart | undefined {
  return Object.prototype.hasOwnProperty.call(OUTPUT_PARTS, portId)
    ? OUTPUT_PARTS[portId]
    : undefined;
}

export function dateDifferencePorts(labels: {
  dateA: string;
  dateB: string;
  years: string;
  months: string;
  days: string;
}): Port[] {
  return [
    {
      id: "input1",
      label: labels.dateA,
      direction: "input",
      valueType: "date",
      value: "",
    },
    {
      id: "input2",
      label: labels.dateB,
      direction: "input",
      valueType: "date",
      value: "",
    },
    {
      id: "outputYears",
      label: labels.years,
      direction: "output",
      valueType: "number",
    },
    {
      id: "outputMonths",
      label: labels.months,
      direction: "output",
      valueType: "number",
    },
    {
      id: "outputDays",
      label: labels.days,
      direction: "output",
      valueType: "number",
    },
  ];
}

/** UTC calendar days keep serialized Dates and date-only literals identical across hosts. */
function calendarDay(value: unknown): Date | null {
  if (!(value instanceof Date) && (typeof value !== "string" || !value.trim()))
    return null;
  const date =
    value instanceof Date
      ? new Date(value.getTime())
      : new Date(value as string);
  if (!Number.isFinite(date.getTime())) return null;
  if (
    typeof value === "string" &&
    /^\d{4}-\d{2}-\d{2}$/.test(value) &&
    date.toISOString().slice(0, 10) !== value
  )
    return null;
  date.setUTCHours(0, 0, 0, 0);
  return date;
}

function addCalendarMonths(date: Date, months: number): Date {
  const result = new Date(date.getTime());
  result.setUTCDate(1);
  result.setUTCMonth(result.getUTCMonth() + months);
  const last = new Date(result.getTime());
  last.setUTCMonth(last.getUTCMonth() + 1, 0);
  result.setUTCDate(Math.min(date.getUTCDate(), last.getUTCDate()));
  return result;
}

/** A − B as whole years, then remaining months and days; reversed inputs negate each part. */
export function dateDifference(
  a: unknown,
  b: unknown,
): Record<DateDifferencePart, number> | null {
  const left = calendarDay(a);
  const right = calendarDay(b);
  if (!left || !right) return null;
  const sign = left.getTime() < right.getTime() ? -1 : 1;
  const start = sign === 1 ? right : left;
  const end = sign === 1 ? left : right;
  let years = end.getUTCFullYear() - start.getUTCFullYear();
  const anniversary = new Date(start.getTime());
  anniversary.setUTCFullYear(start.getUTCFullYear() + years);
  if (anniversary.getTime() > end.getTime()) {
    years -= 1;
    anniversary.setTime(start.getTime());
    anniversary.setUTCFullYear(start.getUTCFullYear() + years);
  }
  let months = Math.min(
    11,
    (end.getUTCFullYear() - anniversary.getUTCFullYear()) * 12 +
      end.getUTCMonth() -
      anniversary.getUTCMonth(),
  );
  let monthStart = addCalendarMonths(anniversary, months);
  if (monthStart.getTime() > end.getTime()) {
    months -= 1;
    monthStart = addCalendarMonths(anniversary, months);
  }
  const days = Math.round((end.getTime() - monthStart.getTime()) / DAY_MS);
  return {
    years: years === 0 ? 0 : sign * years,
    months: months === 0 ? 0 : sign * months,
    days: days === 0 ? 0 : sign * days,
  };
}

export function evaluateDateDifference(
  a: unknown,
  b: unknown,
  part: DateDifferencePart = "years",
): number | undefined {
  return dateDifference(a, b)?.[part];
}
